import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ArticleContent } from '@article/body/main/article-content';
import cls from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { $CurrentVideoArticle } from '@article/body/main/article-video/atoms';
import { ClickPosition } from '~/utils/click-position';
import { useTranslation } from 'react-i18next';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import { VideoContentFooter } from './video-content-footer/index';
import { VideoAuthor } from '@article/body/main/article-video/components/video-content/video-author';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { $VideoFullExpand } from '~/utils/libra/video-full-expand';
import { GptSupplements } from '~/routes/$user_link_name_.$article_id/shared/components/gpt-supplements';

import './style.scss';

export const LONG_READ_MORE_COUNTRY = ['id'];

export const VideoContent: FC = () => {
  const article = useScopedAtomValue($CurrentVideoArticle);
  const { region } = useAtomValue($Locale);
  const { t } = useTranslation();
  const [folded, setFolded] = useState(true);
  const [maskHeight, setMaskHeight] = useState('0');
  const [articleFirstHeight, setArticleFirstHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentExpended = useRef(false);
  const setSimpleCTAModal = useSetAtom($SimpleCTAModalShow);
  const activeToCurrentArticle = useActiveToCurrentArticle();
  const isMobileUser = useAtomValue($IsMobileUser);
  const videoFullExpand = useAtomValue($VideoFullExpand);

  // 判断是否是长read_more翻译国家
  const isLongReadMore = LONG_READ_MORE_COUNTRY.includes(region);

  const articleTitle = useMemo(() => {
    return article.title;
  }, [article, region]);

  useEffect(() => {
    if (!folded) {
      // 显示蒙板
      setMaskHeight('calc(100% + 1px)');
    }
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.style.overflow = folded ? 'hidden' : 'scroll';
        if (folded && !videoFullExpand) {
          setMaskHeight('0');
        }
      }
    });
  }, [folded]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.overflow = 'hidden';
    }
    const $article = contentRef.current?.querySelector('article');
    if ($article) {
      const _el = $article as HTMLDivElement;
      // 获取文字高度
      const $title = document.querySelector('h1.title');
      const p = _el.querySelector('p');

      if ($title) {
        // 文本行数
        const {
          lineHeight: titleLineHeight,
          fontSize: titleFontSize,
          height: titleHeightStr,
        } = window.getComputedStyle($title);

        // 获取行高
        const lineHeight =
          titleLineHeight === 'normal' ? parseFloat(titleFontSize) * 1.14 : parseFloat(titleLineHeight);

        // 获取元素高度
        const titleHeight = parseFloat(titleHeightStr);

        // 计算行数
        const lines = Math.ceil(titleHeight / lineHeight);

        if (lines >= 2) {
          setArticleFirstHeight(0);
          return;
        }
      }

      if (p) {
        const { lineHeight } = window.getComputedStyle(p);
        const _fontSize = parseFloat(lineHeight.replace('px', ''));

        if (!Number.isNaN(_fontSize)) {
          setArticleFirstHeight(Math.max(_fontSize, 0));
        }
      }
    }
  }, [contentRef, folded]);

  useEffect(() => {
    const $content = contentRef.current;
    if (!$content) {
      return;
    }
    const callback = () => {
      setFolded(true);
    };

    $content.addEventListener('click', callback);

    return () => {
      $content.removeEventListener('click', callback);
    };
  }, [contentRef, setFolded]);

  const contentStyle =
    folded && !videoFullExpand
      ? {
          height: `${articleFirstHeight}px`,
        }
      : {};

  return (
    <div
      className={cls('video-article-all-container', {
        folded: !videoFullExpand && folded,
        hitFullExpanded: videoFullExpand,
      })}
    >
      <div
        className={cls('video-article-mask')}
        style={{ height: maskHeight }}
        onClick={() => {
          setFolded(true);
          if (contentRef.current) {
            contentRef.current.scrollTo(0, 0);
          }
        }}
      />
      <div className="video-mask" />
      <div
        className="video-article-container"
        onClick={() => {
          if (!folded) {
            setFolded(true);
            if (contentRef.current) {
              contentRef.current.scrollTo(0, 0);
            }
          }
        }}
        style={
          videoFullExpand
            ? { maxHeight: '95%' }
            : {
                maxHeight: '80%',
              }
        }
      >
        {folded && !videoFullExpand && (
          <div
            className="video-expand"
            onClick={(e: SyntheticEvent) => {
              e.stopPropagation();
              setFolded(false);

              window.Tea('wap_click', {
                current_position: ClickPosition.main_video_see_more,
              });

              if (!contentExpended.current && isMobileUser) {
                setSimpleCTAModal({
                  body: t('seo_stopVideo_modal_desc'),
                  currentPosition: ClickPosition.main_video_see_more_modal,
                  handleClickConfirm: () => activeToCurrentArticle(ClickPosition.main_video_see_more_modal),
                });
                contentExpended.current = true;
              }
            }}
          >
            {t('read_more')}
          </div>
        )}
        <div className="header">
          {!videoFullExpand && <VideoAuthor />}
          <h1 className="title">{articleTitle}</h1>
        </div>
        <div className={cls('video-content', { isLongReadMore })} ref={contentRef} style={contentStyle}>
          <ArticleContent />
          <GptSupplements inVideo />
          <VideoContentFooter folded={!videoFullExpand && folded} />
        </div>
      </div>
    </div>
  );
};
